import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Skeleton, Table, Tag, Button, message, Select, Input } from "antd";
import ManagerLayout from "../Layouts/ManagerLayout";

const { Option } = Select;
const { Search } = Input;

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const ManagerAppointment = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState(""); // Search state
  const [dateFilter, setDateFilter] = useState("all"); // Date filter state
  const [statusFilter, setStatusFilter] = useState([]); // Status column filter state
  const [currentPage, setCurrentPage] = useState(1); // Current page state
  const [pageSize, setPageSize] = useState(50); // Page size state
  const [total, setTotal] = useState(0); // Total number of appointments
  const [refreshKey, setRefreshKey] = useState(0); // Forces a refetch after mutations

  const fetchAppointments = useCallback(
    async (page, size, search, date, statuses) => {
      const name = localStorage.getItem("name");
      const token = localStorage.getItem("token");

      if (!name || !token) {
        console.error("Missing user ID or token in localStorage.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(
          `${backendUrl}/manager/getallappointments`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              name,
              page,
              limit: size,
              search: search || undefined,
              dateFilter: date,
              status: statuses.length ? statuses.join(",") : undefined,
            },
          },
        );

        setAppointments(response.data.appointments || []);
        setTotal(response.data.total || 0);
      } catch (error) {
        console.error(
          "Error fetching appointments:",
          error.response?.data?.message || error.message,
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const prevSearchRef = useRef(searchText);

  useEffect(() => {
    const searchChanged = prevSearchRef.current !== searchText;
    prevSearchRef.current = searchText;

    // Debounce search typing, fetch immediately for every other change
    const timer = setTimeout(
      () => {
        fetchAppointments(
          currentPage,
          pageSize,
          searchText,
          dateFilter,
          statusFilter,
        );
      },
      searchChanged && searchText.trim() !== "" ? 400 : 0,
    );

    return () => clearTimeout(timer);
  }, [
    currentPage,
    pageSize,
    searchText,
    dateFilter,
    statusFilter,
    refreshKey,
    fetchAppointments,
  ]);

  // Reset to first page if the current page no longer exists (e.g. after a filter narrows results)
  useEffect(() => {
    const totalPages = Math.ceil(total / pageSize);
    if (total > 0 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [total, currentPage, pageSize]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    setCurrentPage(1);
  };

  const handleTableChange = (pagination, filters) => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 20);
    setStatusFilter(filters.status || []);
  };

  const handleMarkAsComplete = async (appointmentId) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Missing authentication token.");
      }

      // Send request to mark the appointment as completed
      await axios.post(
        `${backendUrl}/manager/markappointmentcompleted`,
        { appointmentId, status: "completed" },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      message.success("Appointment marked as completed!");
      setRefreshKey((k) => k + 1); // Refresh the current page
    } catch (error) {
      message.error(
        error.response?.data?.message ||
          "Failed to mark appointment as completed.",
      );
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "appointmentId",
      key: "appointmentId",
      ellipsis: true,
      render: (text) => <span>AP{text}</span>,
    },
    {
      title: "Enrollment",
      dataIndex: "enrollment",
      key: "enrollment",
      ellipsis: true,
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      ellipsis: true,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");

        return `${day}/${month}/${year} ${hours}:${minutes}`;
      },
    },
    {
      title: "Manager",
      dataIndex: "manager",
      key: "manager",
      ellipsis: true,
      render: (text) => text || "Not Assigned",
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      ellipsis: true,
    },
    {
      title: "Review",
      key: "action",
      render: (_, record) => {
        // Check if the appointment has a userReview
        const hasReview =
          record.userReview &&
          record.userReview.rating !== 0 &&
          record.userReview.comment.trim() !== "";

        return hasReview ? (
          // If review exists, display the existing rating and message with a checkmark icon
          <div className="flex pl-8 items-center font-semibold space-x-2">
            {record.userReview.comment}
          </div>
        ) : (
          // If no review, show the "Add Review" button
          <div className="flex items-center">N/A</div>
        );
      },
    },
    {
      title: "Slot",
      dataIndex: "slot",
      key: "slot",
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Pending", value: "Pending" },
        { text: "Confirmed", value: "confirmed" },
        { text: "Completed", value: "Completed" },
        { text: "Cancelled", value: "cancelled" },
      ],
      filteredValue: statusFilter,
      render: (status) => {
        const color =
          status === "confirmed"
            ? "blue"
            : status === "Completed"
              ? "green"
              : status === "cancelled"
                ? "red"
                : "orange";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        const isCompleted = record.status.toLowerCase() === "completed";

        return isCompleted ? null : (
          <Button
            type="primary"
            className="bg-gradient-to-r from-blue-800 to-blue-400 hover:from-blue-700 hover:to-blue-500 text-white font-bold shadow-lg hover:shadow-xl"
            onClick={() => handleMarkAsComplete(record.appointmentId)}
          >
            Mark Complete
          </Button>
        );
      },
    },
  ];

  return (
    <ManagerLayout>
      <div className="bg-gray-100 min-h-screen">
        <div className="w-full mb-8 pb-3 px-4 bg-gradient-to-r from-blue-800 to-blue-300 shadow-lg rounded-lg">
          <h1 className="text-2xl pt-4 font-bold text-white">
            All Appointments
          </h1>
          <div className="mt-4 flex items-center space-x-4">
            <Search
              placeholder="Search by Enrollment ID or Appointment ID"
              allowClear
              onChange={handleSearchChange} // Update on input change
              style={{ maxWidth: "400px" }}
            />
            <Select
              defaultValue="all"
              style={{ width: 200 }}
              onChange={handleDateFilterChange}
            >
              <Option value="all">All Dates</Option>
              <Option value="today">Today</Option>
              <Option value="yesterday">Yesterday</Option>
              <Option value="thisWeek">This Week</Option>
              <Option value="thisMonth">This Month</Option>
              <Option value="thisYear">This Year</Option>
            </Select>
            <Button>Total Appointments: {total}</Button>
          </div>
        </div>

        {loading ? (
          <Skeleton active />
        ) : appointments.length > 0 ? (
          <Table
            className="w-full cursor-pointer"
            columns={columns}
            dataSource={appointments}
            rowKey="appointmentId"
            bordered
            scroll={{ x: true }} // Enables table scrolling for small screens
            pagination={{
              current: currentPage,
              pageSize,
              total,
              showSizeChanger: true,
              showTotal: (t) => `Total ${t} appointments`,
            }}
            onChange={handleTableChange}
          />
        ) : (
          <p className="text-center text-gray-500">No appointments found.</p>
        )}
      </div>
    </ManagerLayout>
  );
};

export default ManagerAppointment;
